import { useMutation } from "@apollo/client";
import { CircularProgress, Typography, Tooltip } from "@material-ui/core";
import { ApolloError } from "apollo-boost";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import CloseIcon from '@material-ui/icons/Close';
import styles from './index.module.css';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import { IInvoice, IPaymentInput, PaymentGateway, PaymentMode } from '../../../../reducers/bookings/types';
import { useSnackBar } from '../../../SnackBarContext/SnackBarContext';
import { formatGraphQLErrorMessage, toCurrency } from '../../../utils';
import { CREATE_INVOICE_PAYMENT } from '../../../../graphql/invoices/createPaymentMutation';
import { IAppState } from '../../../../store';
import { SnackBarVariant } from '../../../SnackbarWrapper/SnackbarWrapper';
import StripePayment from '../../../BookingConfirmation/StripePayment';
import { InvoiceType } from "../../NewBooking/Invoices/utils";
import { loadConvergeUS } from "../../../../utils/converge";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProcessingFeeType } from "../../../../reducers/website/types";
import InfoIcon from '@material-ui/icons/Info';
interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  bookingId: string;
  invoice: IInvoice;
  onConfirmation: (invoice: IInvoice) => void;
  returnUrl: string
  getUpdatedInvoice: () => void;
}

const InvoicePaymentDialog: React.FC<IProps> = ({ invoice, open, setOpen, onConfirmation, returnUrl, bookingId, getUpdatedInvoice }) => {
  const snackbar = useSnackBar();
  const history = useHistory();
  const [loading, setLoading] = useState<boolean>(false);
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const { currency, stripeAccountId, autoChargeEnabled, locale, enablePaymentProcessingFee, paymentProcessingFee } = website.organisation;
  const [stripePaymentIntentSecret, setStripePaymentIntentSecret] = useState<string>("");
  const [processingFee, setProcessingFee] = useState<number>(0);
  const {t} = useTranslation();

  useEffect(() => {
    if (invoice) {
      setLoading(true);
      const paymentProcessingFee = applyProcessingFee(invoice.dueAmount, PaymentMode.CARD)
      setProcessingFee(paymentProcessingFee)
      const payment: IPaymentInput = {
        amount: invoice.dueAmount,
        processingFee: enablePaymentProcessingFee ? paymentProcessingFee : 0, 
        booking: bookingId || "",
        currency,
        description: PaymentMode.CARD,
        expireBy: "",
        invoice: invoice.id,
        paymentGateway: website.paymentGateway || PaymentGateway.STRIPE,
        paymentMode: PaymentMode.CARD,
        paymentType: "INWARD",
        successUrl: `${window.location.protocol}//${window.location.host}/account/invoices/${invoice.id}`,
        cancelUrl: `${window.location.protocol}//${window.location.host}/account/invoices/${invoice.id}`
      };
      let isConsolidated = false
      if (invoice.invoiceType === InvoiceType.CONSOLIDATED && invoice.businessCustomer) {
        payment.businessCustomer = invoice.businessCustomer.id;
        isConsolidated = true;
      }
      createPayment({
        variables: {
          payment: {
            ...payment,
            booking: bookingId || ""
          },
          saveDetails: autoChargeEnabled,
          isConsolidated
        }
      });
    }
  }, [invoice])

  const handleDialogClose = () => {
    setOpen(false);
  };

  const applyProcessingFee = (amount: number, paymentMode: PaymentMode) => {
    const paymentGateway = website.paymentGateway
    let currProcessingFee = 0;
    if(!enablePaymentProcessingFee) return 0;
    if (paymentProcessingFee) {
      const modeProcessingDetails = paymentProcessingFee.find((fee) => fee.paymentMode === paymentMode);
      if(modeProcessingDetails) {
        const processingFeeDetails = modeProcessingDetails?.gateways.find((gateway) => gateway.paymentGateway === paymentGateway);
        if (processingFeeDetails) {
          switch(processingFeeDetails.feeType) {
            case ProcessingFeeType.FIXED:
              currProcessingFee = processingFeeDetails.value;
              break;
            case ProcessingFeeType.PERCENTAGE:
              currProcessingFee = (amount * processingFeeDetails.value) / 100;
              break;
            default: 
              currProcessingFee = 0;
          }
        }
      }
    }
    return Math.round(currProcessingFee);
  }

  const stripePromise: any = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "", {
    stripeAccount: stripeAccountId
  });

  const [createPayment, { loading: sessionLoading, data: createPaymentData }] = useMutation(CREATE_INVOICE_PAYMENT, {
    onError: (error: ApolloError) => {
      setLoading(false);
      setOpen(false);
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    },
    onCompleted: async (data) => {
      if (data?.createPayment?.payment?.stripePaymentIntentSecret) {
        // Get Stripe.js instance
        const stripe = await stripePromise;
        // When the customer clicks on the button, redirect them to Checkout.
        if (stripe) {
          setLoading(false)
          setStripePaymentIntentSecret(data.createPayment.payment.stripePaymentIntentSecret)
        }
      } else if (data.createPayment.payment?.convergePaymentSessionUrl) {
        setOpen(false);
        window.location = data.createPayment.payment.convergePaymentSessionUrl;
      } else if (data.createPayment.payment?.convergeUSSessionToken) {
        loadConvergeUS({
          paymentSessionToken: data.createPayment.payment?.convergeUSSessionToken,
          onloadFunction() {
            handleDialogClose();
          },
          redirectFunction() {
            getUpdatedInvoice();
          }
        });
      } else if (data.createPayment.payment?.gpTransactionUrl) {
        setLoading(false);
        setOpen(false);
        window.location = data.createPayment.payment.gpTransactionUrl;
      }
    }
  });

  return (
    <Dialog
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      open={open}
      onClose={handleDialogClose}
      aria-labelledby="form-dialog-title"
    >
      <div className={`${styles.paymentDialog} ${styles.mobile}`}>
        <div className={styles.closeDialog}>
          <Typography variant="h2" className="bold">{t("root:proceedToPay")}</Typography>
          <IconButton onClick={() => handleDialogClose()} aria-label="delete">
            <CloseIcon />
          </IconButton>
        </div>
        <div className="flex padding-left space-between">
          <div>
              <Typography variant="h4" style={{ fontWeight: 'initial' }}>{"Processing Fee"}</Typography>
              <Typography variant="h2" className="bold text-accent">{toCurrency(processingFee, currency, locale)}</Typography>
          </div>
        </div>
        <div className="flex padding-left space-between">
          <div>
            <Typography variant="h4" style={{ fontWeight: 'initial' }}>{t("root:totalPayableAmount")}</Typography>
            <Typography variant="h2" className="bold text-accent">{toCurrency(invoice.dueAmount + processingFee, currency, locale)}</Typography>
          </div>
          <Tooltip title={`${toCurrency(invoice.dueAmount, currency, locale)} + ${toCurrency(processingFee, currency, locale)} = ${toCurrency(invoice.dueAmount + processingFee, currency, locale)}`}>
          <div style={{ marginBottom: 35 }} className="flex padding-left space-between" >
            <div>
              <InfoIcon className={styles.summaryPanelInfoIcon} />
            </div>
          </div>
        </Tooltip>
        </div>
        {loading ? <div style={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </div> : stripePaymentIntentSecret ?
          <StripePayment stripePaymentIntentSecret={stripePaymentIntentSecret} bookingId={bookingId} returnUrl={returnUrl} /> : null}
      </div>
    </Dialog>
  );
};

export default InvoicePaymentDialog;
